/**
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2022 Karmabunny
 */

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type Query = Record<string, any> | URLSearchParams;

export type Body = Query | FormData;

/**
 * API configuration bits.
 */
export interface ApiConfig {
    base_url: string;
    timeout: number;
    base_entity_uid?: string;
    headers?: Record<string, string>;
}

/**
 * API request options.
 */
export interface ApiRequest {
    method: string;
    endpoint: string;
    query?: Query;
    headers?: Record<string, string>,
    body?: any;
    timeout?: number;
}

export class API {

    config: ApiConfig;

    constructor(config?: ApiConfig) {
        if (!config) {
            this.config = {
                base_url: window.__stay__.api_url,
                base_entity_uid: window.__stay__.base_entity_uid,
                timeout: 10_000,
            }
        } else {
            this.config = config;
        }

    }

    /**
     * Fire off a request.
     *
     * Errors galore!:
     * - ApiError on 400+ status codes
     * - TimeoutError if we're too slow
     * - SyntaxError on malformed JSON
     * - TypeError if the network is dead
     *
     * @param req
     * @throws {ApiError|TimeoutError|SyntaxError|TypeError}
     */
    public async call(req: ApiRequest) {

        const abort = new AbortController();

        const { endpoint, method } = req;
        const url = this.config.base_url + endpoint + this.createQuery(req.query);

        // Initial headers.
        const headers: Record<string, string> = {};
        Object.assign(headers, this.config.headers ?? {});

        let body: any = null;

        // Encode the body.
        if (req.body) {
            let contentType: string;
            [body, contentType] = this.createBody(req.body);
            headers['Content-Type'] = contentType;
        }

        // Additional headers, must be after the body bit.
        if (req.headers) {
            Object.assign(headers, req.headers ?? {});
        }

        return await Promise.race([
            // Do a request, although catch status code errors and throw them.
            // Always convert to JSON.
            (async () => {
                const res = await fetch(url, {
                    method, headers, body,
                    signal: abort.signal,
                    credentials: 'include',
                    mode: 'cors',
                });

                if (!res.ok) throw await ApiError.build(url, req, res);

                // TODO we could conditionally convert to JSON based on the
                // 'Accept' header.
                return await res.json();
            })(),

            // Race it. We're on the clock.
            (async (timeout: number) => {
                await new Promise<void>(resolve => setTimeout(resolve, timeout));
                abort.abort();
                throw await ApiError.build(url, req, null);
            })(req.timeout ?? this.config.timeout),
        ]);
    }

    /**
     * Build a query string - from either a URLSearchParams or an key-value object.
     *
     * @param params
     */
    public createQuery(params?: Query): string {
        if (!params) return '';

        if (!(params instanceof URLSearchParams)) {
            params = new URLSearchParams(params);
        }

        const query = params.toString();
        return query ? '?' + query : '';
    }

    /**
     * Build a request body.
     *
     * The return includes the appropriate 'content-type' header.
     *
     * - URLSearchParams - `x-www-form-urlencoded`
     * - FormData - `multipart/form-data`
     * - Object - `application/json`
     *
     * @param body
     * @returns [body, content-type]
     */
    public createBody(body: Body): [any, string] {
        if (body instanceof URLSearchParams) {
            return [
                body.toString(),
                'application/x-www-form-urlencoded',
            ];
        }

        if (body instanceof FormData) {
            return [
                body,
                'multipart/form-data',
            ];
        }

        return [
            JSON.stringify(body),
            'application/json; charset=UTF-8',
        ];
    }
}


export class ApiError extends Error {
    name = 'ApiError';

    status: number;
    url: string;
    res: Response | null;

    static async build(url: string, req: ApiRequest, res: Response | null) {
        let message = '';

        // API responses.
        if (res) {
            try {
                const body = await res.json();

                // Regular errors.
                if (body.message) {
                    message = body.message;
                }
                // Rando errors.
                else if (typeof body.error === "string") {
                    message = body.error;
                }
            }
            catch (e) {}
        }

        // Error parsing JSON body.
        if (!message) {
            message = `${req.method} ${url}`;

            // API generic error.
            if (res) {
                message += ` ${res.status} (${res.statusText})`;
            }
        }

        return new ApiError(message, url, req, res);
    }

    constructor(message: string, url: string, req: ApiRequest, res: Response | null) {
        super(message);
        this.status = res?.status ?? 0;
        this.url = url;
        this.res = res;
    }
}


export class TimeoutError extends ApiError {
    name = 'TimeoutError';

    constructor(req: ApiRequest, url: string, timeout: number) {
        const seconds = (req.timeout ?? timeout / 1000).toFixed(0);
        const message = `Request timed out: ${req.method} ${url} after ${seconds}s`;
        super(message, url, req, null);
    }
}
